import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  modules,
  calcUsedPoints,
  MODULE_CATEGORY_LABELS,
  type ModuleCategory,
} from "@/data/modules";
import { ModuleCard } from "./ModuleCard";
import { ModuleFilterBar } from "./ModuleFilterBar";

const ALL_TAB = "all" as const;

interface ModulePickerProps {
  selectedIds: string[];
  onToggle: (id: string) => void;
  availablePoints: number | null; // null = unbegrenzt
  showCategories?: boolean;
}

export function ModulePicker({
  selectedIds,
  onToggle,
  availablePoints,
  showCategories = true,
}: ModulePickerProps) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<ModuleCategory | typeof ALL_TAB>(ALL_TAB);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // Fetch which modules have help articles
  const { data: moduleArticles } = useQuery({
    queryKey: ["module-help-slugs"],
    queryFn: async () => {
      const { data } = await supabase
        .from("knowledge_articles")
        .select("module_id, slug")
        .not("module_id", "is", null)
        .is("parent_slug", null);
      // Map module_id → main article slug (parent only)
      const map: Record<string, string> = {};
      for (const a of data ?? []) {
        if (a.module_id && !map[a.module_id]) map[a.module_id] = a.slug;
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  const usedPoints = useMemo(() => calcUsedPoints(selectedIds), [selectedIds]);
  const isOverBudget = availablePoints !== null && usedPoints > availablePoints;
  const progressValue = availablePoints !== null ? Math.min((usedPoints / availablePoints) * 100, 100) : 0;

  const filtered = useMemo(() => {
    return modules.filter((m) => {
      const matchesCategory = activeCategory === ALL_TAB || m.category === activeCategory;
      const matchesSearch =
        search.trim() === "" ||
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.shortDescription.toLowerCase().includes(search.toLowerCase()) ||
        m.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
      const matchesSelected = !showSelectedOnly || selectedIds.includes(m.id);
      return matchesCategory && matchesSearch && matchesSelected;
    });
  }, [search, activeCategory, showSelectedOnly, selectedIds]);

  // Group filtered modules by category for display
  const grouped = useMemo(() => {
    if (!showCategories || activeCategory !== ALL_TAB) {
      return { [activeCategory]: filtered } as Record<string, typeof filtered>;
    }
    const result: Record<string, typeof filtered> = {};
    for (const cat of Object.keys(MODULE_CATEGORY_LABELS) as ModuleCategory[]) {
      const items = filtered.filter((m) => m.category === cat);
      if (items.length > 0) result[cat] = items;
    }
    return result;
  }, [filtered, showCategories, activeCategory]);

  return (
    <div className="flex flex-col gap-5">
      {/* ── Header: points progress ── */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">
            Modul-Budget
          </span>
          <span
            className={cn(
              "text-sm font-bold tabular-nums",
              isOverBudget ? "text-destructive" : "text-primary",
            )}
          >
            {usedPoints}
            {availablePoints !== null ? ` / ${availablePoints} Punkte` : " Punkte (unbegrenzt)"}
          </span>
        </div>

        {availablePoints !== null && (
          <Progress
            value={progressValue}
            className={cn(
              "h-2",
              isOverBudget && "[&>div]:bg-destructive",
            )}
          />
        )}

        {isOverBudget && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-destructive/8 border border-destructive/20 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Budget überschritten – Plan upgraden oder Module entfernen.
            </span>
          </div>
        )}
      </div>

      {/* ── Filter bar ── */}
      {showCategories && (
        <ModuleFilterBar
          search={search}
          onSearchChange={setSearch}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          showSelectedOnly={showSelectedOnly}
          onShowSelectedOnlyChange={setShowSelectedOnly}
        />
      )}

      {/* ── Module grid ── */}
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          {showCategories && activeCategory === ALL_TAB && (
            <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
              {MODULE_CATEGORY_LABELS[cat as ModuleCategory] ?? cat}
            </h3>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((m) => (
              <ModuleCard
                key={m.id}
                module={m}
                selected={selectedIds.includes(m.id)}
                onToggle={onToggle}
                helpSlug={moduleArticles?.[m.id]}
              />
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-10">
          Keine Module gefunden – Filter anpassen oder Suche leeren.
        </p>
      )}
    </div>
  );
}
